import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUser } from '../../context/CurrentUserContext';
import { refusalRedirectFor } from '../../utils/routeAccess';
import { useRefusalToast } from './useRefusalToast';

interface NurseOrAdminRouteProps {
  children: React.ReactNode;
}

/**
 * NurseOrAdminRoute component protects routes that require nurse or admin role
 * Extends ProtectedRoute logic by checking for nurse OR admin role
 * Redirects unauthorized users to refusalRedirectFor(roles) (/dashboard; a sales-only user to /crm/partner-enquiries) with an error message
 */
const NurseOrAdminRoute: React.FC<NurseOrAdminRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { roles, isAdmin, isNurse, loading: rolesLoading } = useCurrentUser();
  const location = useLocation();
  const isAuthorized = isNurse || isAdmin;

  // The permission toast fires from an effect, once per refusal (see useRefusalToast)
  useRefusalToast(
    'You do not have permission to access this page. Nurse or Admin role required.',
    !authLoading && !rolesLoading && !!user && !isAuthorized
  );

  // Show loading spinner while checking authentication and roles
  if (authLoading || rolesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  }

  // If authenticated but not nurse or admin, redirect (dashboard; a sales-only user to Partner Enquiries) with error message
  if (!isAuthorized) {
    return <Navigate to={refusalRedirectFor(roles)} replace />;
  }

  // User is authenticated and has nurse or admin role, render children
  return <>{children}</>;
};

export default NurseOrAdminRoute;
